<template>
  <!-- 
    CardConveyor Component
    ======================
    Single Responsibility: Manage infinite scrolling conveyor of card rows
    
    This component:
    - Creates and manages the infinite scroll effect
    - Recycles rows that move off-screen
    - Handles smooth continuous animation
    - Responds to viewport changes
    - Provides the Star Wars crawl 3D effect
  -->
  <div 
    class="card-conveyor"
    :style="conveyorStyles"
    ref="conveyorElement"
  >
    <!-- 
      3D perspective wrapper
      Creates the Star Wars crawl effect
    -->
    <div class="conveyor-perspective">
      <!-- 
        The scrolling track
        Contains all card rows
      -->
      <div 
        class="conveyor-track"
        :style="trackStyles"
        ref="trackElement"
      >
        <!-- 
          Render each row
          Rows are recycled as they scroll off screen
        -->
        <CardRow
          v-for="row in visibleRows"
          :key="row.id"
          :row-id="row.id"
          :cards="row.cards"
          :y-position="row.yPosition"
          :is-visible="row.isVisible"
          :z-index="row.zIndex"
          :opacity="row.opacity"
          :scale="row.scale"
          :rotate-x="row.rotateX"
          :stagger-delay="cardStaggerDelay"
          :eager-load="row.eagerLoad"
          @row-loaded="handleRowLoaded"
          @card-loaded="handleCardLoaded"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive, onMounted, onUnmounted, nextTick } from 'vue'
import CardRow from './CardRow.vue'
import { ANIMATION_CONFIG } from './animation-config.js'

export default {
  name: 'CardConveyor',
  
  components: {
    CardRow
  },
  
  props: {
    // Array of card data to display
    cardData: {
      type: Array,
      required: true
    },
    
    // Number of cards per row (can be dynamic)
    cardsPerRow: {
      type: Number,
      default: 12
    },
    
    // Speed of scrolling in pixels per frame
    scrollSpeed: {
      type: Number,
      default: 0.8
    },
    
    // Height of each row in pixels
    rowHeight: {
      type: Number,
      default: ANIMATION_CONFIG.spacing.rowHeight
    },
    
    // Delay between card animations in milliseconds
    cardStaggerDelay: {
      type: Number,
      default: ANIMATION_CONFIG.visual.cardStaggerDelay
    },
    
    // Whether to pause scrolling
    paused: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['ready', 'row-recycled', 'viewport-changed'],
  
  setup(props, { emit }) {
    // Element refs
    const conveyorElement = ref(null)
    const trackElement = ref(null)
    
    // Viewport tracking
    const viewport = reactive({
      width: 0,
      height: 0,
      rows: 0 // Number of rows needed to fill viewport
    })
    
    // Row management
    const rows = ref([])
    const rowIdCounter = ref(0)
    const scrollPosition = ref(0)
    let animationFrameId = null
    let resizeObserver = null
    let rowAdditionInterval = null
    let lastRowAddTime = 0
    let lastFrameTime = 0
    let frameCount = 0
    let isDocumentVisible = true
    let memoryCheckInterval = null
    
    // Responsive row addition delay using config
    const getRowAddDelay = () => {
      const width = viewport.width || window.innerWidth
      const { breakpoints } = ANIMATION_CONFIG
      const { rowAddDelay } = ANIMATION_CONFIG.timing
      
      if (width < breakpoints.mobile_small) return rowAddDelay.mobile_small
      if (width < breakpoints.tablet) return rowAddDelay.mobile
      if (width < breakpoints.desktop_small) return rowAddDelay.tablet
      return rowAddDelay.desktop
    }
    
    // Get row spacing based on viewport width
    const getRowSpacing = () => {
      const width = viewport.width || window.innerWidth
      const { breakpoints, spacing } = ANIMATION_CONFIG
      
      if (width < breakpoints.mobile) return spacing.rowGap.mobile_small
      if (width < breakpoints.desktop_small) return spacing.rowGap.mobile
      if (width < breakpoints.desktop_medium) return spacing.rowGap.tablet
      if (width < breakpoints.desktop_large) return spacing.rowGap.desktop
      return spacing.rowGap.desktop_large
    }
    
    const cardPool = ref([]) // Pool of shuffled cards for variety
    
    /**
     * Calculate how many rows we need to fill the viewport
     * Add buffer rows above and below for smooth scrolling
     * Properly handles initial load and responsive changes
     */
    const calculateViewportRows = () => {
      // Handle initial load when element might not be ready
      if (!conveyorElement.value) {
        // Use window dimensions as fallback on initial load
        viewport.width = window.innerWidth
        viewport.height = window.innerHeight * 0.8 // Approximate hero height
      } else {
        const rect = conveyorElement.value.getBoundingClientRect()
        viewport.width = rect.width
        viewport.height = rect.height
      }
      
      // Calculate rows needed with buffer based on viewport size
      const visibleRows = Math.ceil(viewport.height / props.rowHeight)
      const bufferRows = viewport.width < ANIMATION_CONFIG.breakpoints.tablet ? 
        ANIMATION_CONFIG.spacing.bufferRows.mobile : 
        ANIMATION_CONFIG.spacing.bufferRows.desktop
      viewport.rows = visibleRows + (bufferRows * 2)
      
      emit('viewport-changed', { ...viewport })
    }
    
    /**
     * Initialize card pool with shuffled cards for variety
     * Creates a large pool to ensure continuous variety
     */
    const initializeCardPool = () => {
      cardPool.value = []
      // Create many copies for continuous variety
      for (let i = 0; i < ANIMATION_CONFIG.performance.cardPoolMultiplier; i++) {
        // Shuffle order for each copy
        const shuffled = [...props.cardData].sort(() => Math.random() - 0.5)
        cardPool.value.push(...shuffled.map((card, idx) => ({
          ...card,
          poolId: `${i}-${idx}-${Date.now()}`
        })))
      }
      // Final shuffle of entire pool
      for (let i = cardPool.value.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [cardPool.value[i], cardPool.value[j]] = [cardPool.value[j], cardPool.value[i]]
      }
    }
    
    /**
     * Get next set of unique cards from the pool
     * Implements seamless infinite cycling
     */
    const getNextCards = (count) => {
      // Refill pool if running low
      if (cardPool.value.length < count * 2) {
        // Add more shuffled cards to the end
        const shuffled = [...props.cardData].sort(() => Math.random() - 0.5)
        cardPool.value.push(...shuffled.map((card, idx) => ({
          ...card,
          poolId: `refresh-${Date.now()}-${idx}`
        })))
      }
      
      const cards = []
      for (let i = 0; i < count; i++) {
        // Take from front
        const card = cardPool.value.shift()
        cards.push(card)
        // Add back to end for infinite cycling
        cardPool.value.push({
          ...card,
          poolId: `${card.poolId}-cycled`
        })
      }
      return cards
    }
    
    /**
     * Create a new row of cards with better variety
     * Pulls from the card pool to ensure no duplicates
     * Includes rotation and facing properties for 3D effect
     */
    const createRow = (yPosition = 0, recycled = false, fadeIn = false) => {
      const id = `row-${rowIdCounter.value++}-${Date.now()}`
      const cards = getNextCards(props.cardsPerRow).map((card, i) => ({
        ...card,
        uniqueId: `${id}-card-${i}`
      }))
      
      return {
        id,
        cards,
        yPosition,
        isVisible: !fadeIn,
        fadeIn,
        zIndex: 0,
        opacity: fadeIn ? 0 : 1,
        scale: fadeIn ? 0.9 : 1,
        eagerLoad: !recycled,
        recycled,
        createdAt: Date.now(),
        speed: 1,
        rotateX: 0, // Start facing forward
        facing: 'forward' // Track facing state
      }
    }
    
    /**
     * Initialize rows - fill the viewport initially
     * Responsive to viewport size on first load
     */
    const initializeRows = () => {
      rows.value = []
      scrollPosition.value = 0
      initializeCardPool()
      
      // Ensure viewport is calculated
      if (viewport.height === 0) {
        calculateViewportRows()
      }
      
      // Get consistent row spacing that will be used everywhere
      const rowSpacing = getRowSpacing()
      const totalRowHeight = props.rowHeight + rowSpacing
      
      // Calculate how many rows we need to fill viewport + buffer
      const visibleRows = Math.ceil(viewport.height / totalRowHeight)
      const bufferRows = 3 // Extra rows for smooth scrolling
      const rowsNeeded = visibleRows + bufferRows
      
      // Create initial rows starting from just below viewport
      // This matches where addNewRowAtBottom will place new rows
      for (let i = 0; i < rowsNeeded; i++) {
        // Start from bottom and place rows upward with exact same spacing as continuous
        // Use same calculation as addNewRowAtBottom for consistency
        const yPos = viewport.height + rowSpacing - (i * totalRowHeight)
        const newRow = createRow(yPos, false, false)
        newRow.opacity = 1
        newRow.isVisible = true
        newRow.fadeIn = false
        newRow.rotateX = 0
        newRow.facing = 'flat'
        rows.value.push(newRow)
      }
      
      updateRowZIndices()
    }
    
    /**
     * Update z-indices for proper layering
     * Newer/bottom rows have higher z-index to appear above older rows
     */
    const updateRowZIndices = () => {
      rows.value.forEach((row, index) => {
        // Higher index (bottom rows) get higher z-index
        row.zIndex = index + 1
      })
    }
    
    /**
     * The main animation loop with bezier curve motion
     * Handles continuous scrolling with easing
     * Includes RAF throttling and visibility checks
     */
    const animate = (timestamp) => {
      // Pause if document is hidden or animation is paused
      if (props.paused || !isDocumentVisible) {
        animationFrameId = requestAnimationFrame(animate)
        return
      }
      
      // RAF throttling - skip frames if running too fast
      const targetFrameTime = 1000 / ANIMATION_CONFIG.timing.targetFPS
      if (timestamp - lastFrameTime < targetFrameTime * 0.9) {
        animationFrameId = requestAnimationFrame(animate)
        return
      }
      lastFrameTime = timestamp
      
      const currentTime = Date.now()
      
      // Update scroll position
      scrollPosition.value += props.scrollSpeed
      
      // Only update visible rows for performance
      const buffer = props.rowHeight * ANIMATION_CONFIG.performance.updateBuffer
      const visibleForUpdate = rows.value.filter(row => 
        row.yPosition > -buffer && row.yPosition < viewport.height + buffer
      )
      
      // Update visible row positions with bezier curve acceleration
      visibleForUpdate.forEach((row, index) => {
        // Calculate time-based acceleration
        const age = (currentTime - row.createdAt) / 1000 // Age in seconds
        
        // Responsive acceleration based on viewport
        const isMobile = viewport.width < ANIMATION_CONFIG.breakpoints.tablet
        const isTablet = viewport.width < ANIMATION_CONFIG.breakpoints.desktop_small
        const { acceleration, accelerationPhases } = ANIMATION_CONFIG.motion
        
        let speedMultiplier
        if (age < accelerationPhases.initial) {
          // Slow initial appearance for smooth entry
          speedMultiplier = isMobile ? acceleration.initial.mobile : acceleration.initial.desktop
        } else if (age < accelerationPhases.accelerating) {
          // Gradual acceleration
          const t = (age - accelerationPhases.initial) / (accelerationPhases.accelerating - accelerationPhases.initial)
          const maxSpeed = isMobile ? acceleration.accelerating.mobile_max : 
                         (isTablet ? acceleration.accelerating.tablet_max : acceleration.accelerating.desktop_max)
          const minSpeed = isMobile ? acceleration.accelerating.mobile_min : acceleration.initial.desktop
          speedMultiplier = minSpeed + (t * (maxSpeed - minSpeed))
        } else {
          // Normal constant speed
          speedMultiplier = isMobile ? acceleration.normal.mobile : 
                         (isTablet ? acceleration.normal.tablet : acceleration.normal.desktop)
        }
        
        // Move upward (subtract instead of add)
        row.yPosition -= props.scrollSpeed * speedMultiplier * row.speed
        
          // Handle fade-in for new rows
        if (row.fadeIn) {
          const fadeProgress = Math.min((age * ANIMATION_CONFIG.timing.fadeInSpeed), 1)
          row.opacity = ANIMATION_CONFIG.visual.fadeOpacity.start + 
                       (fadeProgress * (ANIMATION_CONFIG.visual.fadeOpacity.end - ANIMATION_CONFIG.visual.fadeOpacity.start))
          row.scale = ANIMATION_CONFIG.visual.fadeScale.start + 
                     (fadeProgress * (ANIMATION_CONFIG.visual.fadeScale.end - ANIMATION_CONFIG.visual.fadeScale.start))
          
          if (fadeProgress >= 1) {
            row.fadeIn = false
            row.isVisible = true
            row.scale = ANIMATION_CONFIG.visual.fadeScale.end
          }
        }
        
        // No rotation - keep all rows flat
        row.rotateX = 0
      })
      
      // Clean up rows that have scrolled off the top OR are older than configured expiration
      const topThreshold = -props.rowHeight * 2
      const maxAge = ANIMATION_CONFIG.timing.rowExpiration
      const prevLength = rows.value.length
      
      rows.value = rows.value.filter(row => {
        const age = currentTime - row.createdAt
        // Remove if off screen OR older than 30 seconds
        const shouldKeep = row.yPosition > topThreshold && age < maxAge
        
        // Clear row resources if removing
        if (!shouldKeep) {
          row.cards = null
          row.isVisible = false
        }
        
        return shouldKeep
      })
      
      // Log cleanup if rows were removed
      if (prevLength > rows.value.length) {
        console.debug(`Cleaned up ${prevLength - rows.value.length} expired rows`)
      }
      
      // Add new rows continuously at the bottom
      if (currentTime - lastRowAddTime > getRowAddDelay()) {
        addNewRowAtBottom()
        lastRowAddTime = currentTime
      }
      
      // Track frame rate
      frameCount++
      
      // Continue animation
      animationFrameId = requestAnimationFrame(animate)
    }
    
    /**
     * Check if any rows need to be recycled
     * Moves rows from bottom to top when they go off screen
     */
    const checkRowRecycling = () => {
      const bottomThreshold = viewport.height + props.rowHeight * 2
      const topPosition = -props.rowHeight * 2
      
      rows.value.forEach(row => {
        // Row has scrolled off the bottom
        if (row.yPosition > bottomThreshold) {
          recycleRow(row, topPosition)
        }
      })
    }
    
    /**
     * Recycle a row by moving it to a new position
     * and giving it new card data
     */
    const recycleRow = (row, newYPosition) => {
      // Update position
      row.yPosition = newYPosition
      
      // Get new cards for this row
      const startIndex = (rowIdCounter.value * props.cardsPerRow) % props.cardData.length
      row.cards = []
      
      for (let i = 0; i < props.cardsPerRow; i++) {
        const dataIndex = (startIndex + i) % props.cardData.length
        row.cards.push({ ...props.cardData[dataIndex] })
      }
      
      // Update row properties
      row.id = `row-${rowIdCounter.value++}`
      row.recycled = true
      row.eagerLoad = false
      
      // Re-sort rows by position
      rows.value.sort((a, b) => a.yPosition - b.yPosition)
      updateRowZIndices()
      
      emit('row-recycled', row.id)
    }
    
    /**
     * Add a new row at the bottom with fade-in effect
     * Used for continuous generation
     * Uses exact same spacing as initial rows
     */
    const addNewRowAtBottom = () => {
      // Get consistent row spacing - same as used in initializeRows
      const rowSpacing = getRowSpacing()
      
      // Find the lowest row position if any exist
      let bottomPosition = viewport.height + rowSpacing
      
      if (rows.value.length > 0) {
        // Find the current bottom-most row
        const bottomRow = rows.value.reduce((lowest, row) => 
          row.yPosition > lowest.yPosition ? row : lowest
        )
        // Place new row exactly one row height + spacing below it
        bottomPosition = bottomRow.yPosition + props.rowHeight + rowSpacing
      }
      
      const newRow = createRow(bottomPosition, false, true)
      
      // Start with fade-in animation
      newRow.opacity = 0
      newRow.scale = 1 // Keep scale at 1 to maintain consistent spacing
      newRow.isVisible = false
      newRow.fadeIn = true
      newRow.rotateX = 0
      newRow.facing = 'flat'
      
      // Add to end of array (bottom of conveyor)
      rows.value.push(newRow)
      updateRowZIndices()
      
      // Keep a reasonable number of rows for seamless looping
      const maxRows = Math.max(viewport.rows + ANIMATION_CONFIG.spacing.maxRows.buffer, ANIMATION_CONFIG.spacing.maxRows.minimum)
      const currentTime = Date.now()
      const maxAge = ANIMATION_CONFIG.timing.rowExpiration
      
      if (rows.value.length > maxRows) {
        // Remove oldest rows that have moved off the top OR are too old
        const topThreshold = -props.rowHeight * 2
        rows.value = rows.value.filter(row => {
          const age = currentTime - row.createdAt
          return row.yPosition > topThreshold && age < maxAge
        })
      }
    }
    
    /**
     * Handle row loaded event
     */
    const handleRowLoaded = (rowId) => {
      // Could trigger loading of next row
      // or other orchestration logic
    }
    
    /**
     * Handle individual card loaded event
     */
    const handleCardLoaded = ({ rowId, cardId }) => {
      // Track loading progress if needed
    }
    
    /**
     * Handle viewport resize
     * Completely resets animation to ensure proper spacing
     */
    const handleResize = () => {
      const previousHeight = viewport.height
      const previousWidth = viewport.width
      
      calculateViewportRows()
      
      // Check if resize is significant (more than 50px change)
      const heightChange = Math.abs(viewport.height - previousHeight)
      const widthChange = Math.abs(viewport.width - previousWidth)
      
      // For any significant resize, completely reset the animation
      // This ensures consistent spacing that matches initial load
      if (heightChange > 50 || widthChange > 50) {
        console.log('Significant resize detected - resetting animation')
        
        // Cancel current animation
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId)
          animationFrameId = null
        }
        
        // Clear all existing rows
        rows.value.forEach(row => {
          row.cards = null
          row.isVisible = false
        })
        rows.value = []
        
        // Reset counters
        scrollPosition.value = 0
        rowIdCounter.value = 0
        lastRowAddTime = 0
        lastFrameTime = 0
        frameCount = 0
        
        // Reinitialize with proper spacing
        initializeRows()
        
        // Restart animation after a brief delay
        setTimeout(() => {
          lastFrameTime = performance.now()
          lastRowAddTime = Date.now()
          animate(lastFrameTime)
        }, 100)
      }
      
      // Emit resize event for parent component
      emit('viewport-changed', { ...viewport })
    }
    
    /**
     * Debounced resize handler
     */
    const debouncedResize = (() => {
      let timeout
      return () => {
        clearTimeout(timeout)
        timeout = setTimeout(handleResize, ANIMATION_CONFIG.timing.resizeDebounce)
      }
    })()
    
    /**
     * Computed styles for the conveyor
     */
    const conveyorStyles = computed(() => ({
      '--row-height': `${props.rowHeight}px`,
      '--scroll-speed': `${props.scrollSpeed}px`
    }))
    
    /**
     * Computed styles for the track
     */
    const trackStyles = computed(() => ({
      // Smooth GPU-accelerated transform
      transform: 'translateZ(0)'
    }))
    
    /**
     * Visible rows - optimized to only return rows actually in viewport
     */
    const visibleRows = computed(() => {
      // Only return rows that are actually visible in the viewport
      const buffer = props.rowHeight * ANIMATION_CONFIG.performance.visibilityBuffer
      return rows.value.filter(row => {
        return row.yPosition > -buffer && row.yPosition < viewport.height + buffer
      })
    })
    
    /**
     * Start progressive row loading
     * Adds rows one at a time with smooth timing
     */
    const startProgressiveLoading = () => {
      // Start adding rows immediately, one at a time
      let isFirstRow = true
      
      const addFirstRows = () => {
        if (props.paused) {
          setTimeout(addFirstRows, 100)
          return
        }
        
        // Add the first row to start the effect
        if (isFirstRow && rows.value.length === 0) {
          addNewRowAtTop()
          isFirstRow = false
          lastRowAddTime = Date.now()
        }
      }
      
      // Start the progressive loading
      setTimeout(addFirstRows, ANIMATION_CONFIG.timing.initialDelay)
    }
    
    /**
     * Setup ResizeObserver for responsive behavior
     */
    const setupResizeObserver = () => {
      if (!conveyorElement.value) return
      
      resizeObserver = new ResizeObserver(debouncedResize)
      resizeObserver.observe(conveyorElement.value)
    }
    
    /**
     * Handle document visibility changes
     * Pauses animation when tab is hidden to save resources
     */
    const handleVisibilityChange = () => {
      isDocumentVisible = !document.hidden
      if (isDocumentVisible) {
        console.log('Tab visible - resuming animation')
        // Reset timing to prevent jumps
        lastFrameTime = performance.now()
        lastRowAddTime = Date.now()
      } else {
        console.log('Tab hidden - pausing animation')
      }
    }
    
    /**
     * Monitor memory usage and clean up if needed
     */
    const monitorMemory = () => {
      if (!performance.memory) return // Not supported in all browsers
      
      const memoryInfo = {
        used: Math.round(performance.memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(performance.memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576) // MB
      }
      
      const usage = (memoryInfo.used / memoryInfo.limit) * 100
      
      // Log if memory usage is high
      if (usage > ANIMATION_CONFIG.performance.memoryWarningThreshold) {
        console.warn(`High memory usage: ${usage.toFixed(1)}% (${memoryInfo.used}MB / ${memoryInfo.limit}MB)`)
        
        // Force cleanup of old rows
        const currentTime = Date.now()
        const aggressiveMaxAge = ANIMATION_CONFIG.timing.aggressiveExpiration
        
        rows.value = rows.value.filter(row => {
          const age = currentTime - row.createdAt
          if (age > aggressiveMaxAge) {
            row.cards = null
            row.isVisible = false
            return false
          }
          return true
        })
      }
      
      // Track FPS
      if (frameCount > 0) {
        const fps = frameCount
        frameCount = 0
        
        if (fps < ANIMATION_CONFIG.performance.fpsWarningThreshold) {
          console.warn(`Low FPS detected: ${fps}`)
        }
      }
    }
    
    // Lifecycle hooks
    onMounted(async () => {
      // Initialize viewport dimensions immediately
      calculateViewportRows()
      
      await nextTick()
      
      // Recalculate with actual element dimensions
      if (conveyorElement.value) {
        calculateViewportRows()
      }
      
      // Initialize rows based on viewport
      initializeRows()
      
      // Setup observers for future changes
      setupResizeObserver()
      
      // Add window resize listener as backup
      window.addEventListener('resize', debouncedResize)
      
      // Add orientation change listener for mobile devices
      window.addEventListener('orientationchange', () => {
        console.log('Orientation change detected')
        // Force immediate reset on orientation change
        setTimeout(() => {
          handleResize()
        }, 100)
      })
      
      // Add visibility change listener
      document.addEventListener('visibilitychange', handleVisibilityChange)
      
      // Start memory monitoring in development
      if (process.env.NODE_ENV === 'development' && performance.memory) {
        memoryCheckInterval = setInterval(monitorMemory, ANIMATION_CONFIG.performance.memoryCheckInterval)
      }
      
      // Small delay to ensure smooth start on different devices
      setTimeout(() => {
        lastFrameTime = performance.now()
        animate(lastFrameTime)
        startProgressiveLoading()
      }, 100)
      
      // Emit ready event
      emit('ready')
    })
    
    onUnmounted(() => {
      // Cleanup
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
      
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
      
      if (rowAdditionInterval) {
        clearInterval(rowAdditionInterval)
      }
      
      // Remove all listeners
      window.removeEventListener('resize', debouncedResize)
      window.removeEventListener('orientationchange', handleResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      
      // Clear memory monitoring
      if (memoryCheckInterval) {
        clearInterval(memoryCheckInterval)
      }
    })
    
    return {
      conveyorElement,
      trackElement,
      visibleRows,
      conveyorStyles,
      trackStyles,
      handleRowLoaded,
      handleCardLoaded
    }
  }
}
</script>

<style scoped>
/**
 * Card Conveyor Styles
 * Creates the infinite scroll effect with 3D perspective
 */

.card-conveyor {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
}

/* 3D perspective container */
.conveyor-perspective {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  perspective: 800px;
  perspective-origin: 50% 90%; /* Higher origin to see bottom cards better */
}

/* The scrolling track - no rotation here, rows handle their own */
.conveyor-track {
  position: absolute;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  /* Remove global rotation - let rows control their own */
  transform: translateZ(0);
  
  /* GPU acceleration */
  will-change: transform;
  backface-visibility: hidden;
}

/* Gradient overlays for depth - removed, will apply to wrapper instead */

/* Subtle pulsing animation for top gradient */
@keyframes pulse-gradient {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.85; }
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .conveyor-perspective {
    perspective: 800px;
    perspective-origin: 50% 40%;
  }
  
  .conveyor-track {
    transform: rotateX(25deg);
  }
}

/* Large screens */
@media (min-width: 1920px) {
  .conveyor-perspective {
    perspective: 1500px;
  }
}

/* Performance optimizations */
.card-conveyor * {
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* Debug mode - uncomment to see conveyor boundaries */
/*
.card-conveyor {
  border: 2px solid red;
}

.conveyor-track {
  border: 2px solid blue;
}
*/
</style>